import type { UniqueIdentifier } from '@dnd-kit/core';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { TreeItem } from 'performant-array-to-tree';
import { EtimReleasesEtimFeatureFeatureTypeChoices, FeatureType, QuotationGroupFeatureType, ItemTypeChoices } from '../__generated__/graphql';
import { FlattenedItem } from '../components/shared/dnd/types';
import { MaxUncollapsedDepthSource } from '../constants';
import { getLogicalLabel } from '../helpers/utils';

interface TargetOrder {
  targetOrder?: number
}

interface FeatureEditType {
  item: QuotationGroupFeatureType,
  groupId: string,
}

export interface QuotationGroupFeature {
  feature: FeatureType,
  value: string | number | boolean | null | number[],
  id?: ID
}


interface QuotationState {
  selectedGroupFeature?: QuotationGroupFeature,
  copiedItem: FlattenedItem | null,
  selectedItem: FlattenedItem | null,
  pasteSearchResultsBelowSelectedItem: boolean, 
  itemToCut: FlattenedItem | null,
  itemToSinglePasteBelow: { 
    parentId: UniqueIdentifier | null;
    order?: number;
    externalId: string;
    id: UniqueIdentifier
  },
  maximumQuotationUncollapsedDepth: number,
  maximumQuotationListStaticItemsUncollapsedDepth: number,
  maximumQuotationListDndItemsUncollapsedDepth: number,
  isEstimateMode: boolean,
  quotationItemsTreeLength: number,
  isLoadingUpdate: boolean,
  isGroupSuggestionOpened: boolean,
  isInfoOverlayOpen: boolean,
  isProjectLogOverlayOpen: boolean,
  isCreateItemOverlayOpen: boolean,
  groupItemIdForSuggestion: string,
  activeQuotationGroupId: string,
  groupFeatureIdToEdit: string,
  featureOptionToEdit: SelectOption | undefined,
  featureValueOptionToEdit: SelectOption | number | undefined,
  featureToEdit: FeatureType | undefined,
  showDeleteModal: boolean,
  showCreateGroupModal: boolean,
  showCreateServiceModal: boolean,
  showCreateQuotationModal: boolean,
  showGeneratePDFModal: boolean,
  showSelectFeatureModal: boolean,
  showDeleteFeatureModal: boolean,
  showCreateGTConfirmationModal: boolean,
  itemIdToCreateGTFrom: string,
  newGroupOrder?: number,
  newServiceOrder?: number,
  newProductOrder?: number,
  createNewItemInside: boolean,
  // treeItems is used for navigation through quotation using arrow keys
  treeItems: TreeItem[],
  openedDeatilsItems: ID[],
  itemToCollapse: ID,
  itemToUncollapse: ID,
  collapsedItemsId: UniqueIdentifier[],
  isQuotationReadOnly: boolean,
  productSuggestionQuantity: number,
  dndGroupId: UniqueIdentifier,
  isComposeMode: boolean,
  showFullSummary: boolean,
  quotationPDFName: string,
  isAnyGroupBeingExported: boolean,
}

// Define the initial state using that type
const initialState: QuotationState = {
  copiedItem: null,
  selectedItem: null,
  pasteSearchResultsBelowSelectedItem: true,
  itemToCut: null,
  itemToSinglePasteBelow: { parentId: '', order: 0, externalId: '', id: '' },
  maximumQuotationUncollapsedDepth: 0,
  maximumQuotationListStaticItemsUncollapsedDepth: 0,
  maximumQuotationListDndItemsUncollapsedDepth: 0,
  isEstimateMode: false,
  quotationItemsTreeLength: 0,
  isLoadingUpdate: false,
  isGroupSuggestionOpened: false,
  isInfoOverlayOpen: false,
  isProjectLogOverlayOpen: false,
  isCreateItemOverlayOpen: false,
  groupItemIdForSuggestion: '',
  activeQuotationGroupId: '',
  groupFeatureIdToEdit: '',
  featureOptionToEdit: undefined,
  featureValueOptionToEdit: undefined,
  featureToEdit: undefined,
  showDeleteModal: false,
  showCreateGroupModal: false,
  showCreateServiceModal: false,
  showCreateQuotationModal: false,
  showGeneratePDFModal: false,
  showSelectFeatureModal: false,
  showDeleteFeatureModal: false,
  showCreateGTConfirmationModal: false,
  itemIdToCreateGTFrom: '',
  createNewItemInside: false,
  treeItems: [],
  openedDeatilsItems: [],
  itemToCollapse: '',
  itemToUncollapse: '',
  collapsedItemsId: [],
  isQuotationReadOnly: false,
  productSuggestionQuantity: 1,
  dndGroupId: '',
  isComposeMode: false,
  showFullSummary: false,
  quotationPDFName: '',
  isAnyGroupBeingExported: false,
};

const quotationSlice = createSlice({
  name: 'quotation',
  initialState: initialState,
  reducers: {
    setSelectedGroupFeature: (state, action: PayloadAction<QuotationGroupFeature | undefined>) => {
      state.selectedGroupFeature = action.payload;
    },
    setCopiedItem: (state, action: PayloadAction<FlattenedItem>) => {
      state.copiedItem = action.payload;
    },
    setItemToCut: (state, action: PayloadAction<FlattenedItem>) => {
      state.itemToCut = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<FlattenedItem>) => {
      state.selectedItem = action.payload;
      if (action.payload.itemType === ItemTypeChoices.Group) {
        state.pasteSearchResultsBelowSelectedItem = false;
      } else {
        state.pasteSearchResultsBelowSelectedItem = true;
      }
    },
    setPasteSearchResultsBelowSelectedItem: (state, action: PayloadAction<boolean>) => {
      state.pasteSearchResultsBelowSelectedItem = action.payload;
    },
    unsetCopiedItem: (state) => {
      state.copiedItem = null;
    },
    unsetItemToCut: (state) => {
      state.itemToCut = null;
    },
    unsetSelectedItem: (state) => {
      state.selectedItem = null;
    },
    setItemToSinglePasteBelow: (state, action: PayloadAction<FlattenedItem>) => {
      const createNewItemInside = state.createNewItemInside;
      const orderToPaste = createNewItemInside ? action.payload.children.length : action.payload.order as number + 1;
      state.itemToSinglePasteBelow.parentId = action.payload.parentId;
      state.itemToSinglePasteBelow.order = orderToPaste;
      state.itemToSinglePasteBelow.externalId = action.payload.product?.externalId as string;
      state.itemToSinglePasteBelow.id = action.payload.id;
    },
    unsetItemToSinglePasteBelow: (state) => {
      state.itemToSinglePasteBelow.parentId = '';
      state.itemToSinglePasteBelow.order = 0;
      state.itemToSinglePasteBelow.externalId = '';
      state.itemToSinglePasteBelow.id = '';
    },
    setMaximumQuotationUncollapsedDepth: (state, action: PayloadAction<{ 
      maxDepth: number, 
      source?: MaxUncollapsedDepthSource,
    }>) => {
      if (action.payload.source) {
        if (action.payload.source === MaxUncollapsedDepthSource.QuotationListStaticItem) {
          state.maximumQuotationListStaticItemsUncollapsedDepth = action.payload.maxDepth;
        }
        if (action.payload.source === MaxUncollapsedDepthSource.QuotationListDndItem) {
          state.maximumQuotationListDndItemsUncollapsedDepth = action.payload.maxDepth;
        }

        state.maximumQuotationUncollapsedDepth = Math.max(
          state.maximumQuotationListStaticItemsUncollapsedDepth, 
          state.maximumQuotationListDndItemsUncollapsedDepth,
        );
      } else {
        state.maximumQuotationUncollapsedDepth = action.payload.maxDepth;
      }
    },
    setEstimateMode: (state, action: PayloadAction<boolean>) => {
      state.isEstimateMode = action.payload;
    },
    setQuotationItemsTreeLength: (state, action: PayloadAction<number>) => {
      state.quotationItemsTreeLength = action.payload;
    },
    setIsLoadingUpdate: (state, action: PayloadAction<boolean>) => {
      state.isLoadingUpdate = action.payload;
    },
    setIsGroupSuggestionOpened: (state, action: PayloadAction<boolean>) => {
      state.isGroupSuggestionOpened = action.payload;
    },
    setGroupItemIdForSuggestion: (state, action: PayloadAction<string>) => {
      state.groupItemIdForSuggestion = action.payload;
    },
    setActiveQuotationGroupId: (state, action: PayloadAction<string>) => {
      state.activeQuotationGroupId = action.payload;
    },
    setFeatureEdit: (state, action: PayloadAction<FeatureEditType | undefined>) => {
      if (action) {
        state.activeQuotationGroupId = action.payload?.groupId as string;
        state.groupFeatureIdToEdit = action.payload?.item.id as string;
        state.featureOptionToEdit = { value: action.payload?.item.etimFeature.id, label: action.payload?.item.etimFeature.localizedDescription as string };
        const option =
          action.payload?.item.etimFeature.featureType === EtimReleasesEtimFeatureFeatureTypeChoices.Numeric || 
          action.payload?.item.etimFeature.featureType === EtimReleasesEtimFeatureFeatureTypeChoices.Range 
            ? action.payload?.item.value
            : { value: action.payload?.item.value, label: getLogicalLabel(action.payload?.item.value) as string };
        state.featureValueOptionToEdit = option;
        state.featureToEdit = action.payload?.item.etimFeature;
      } else {
        state.activeQuotationGroupId = '';
        state.groupFeatureIdToEdit = '';
        state.featureOptionToEdit = undefined;
        state.featureValueOptionToEdit = undefined;
        state.featureToEdit = undefined;
      }
    },
    setGroupFeatureIdToEdit: (state, action: PayloadAction<string>) => {
      state.groupFeatureIdToEdit = action.payload;
    },
    setShowDeleteModal: (state, action: PayloadAction<boolean>) => {
      state.showDeleteModal = action.payload;
    },
    setShowCreateGroupModal: (state, action: PayloadAction<boolean>) => {
      state.showCreateGroupModal = action.payload;
    },
    setShowCreateServiceModal: (state, action: PayloadAction<boolean>) => {
      state.showCreateServiceModal = action.payload;
    },
    openCreateServiceModal: (state, action: PayloadAction<TargetOrder>) => {
      state.newServiceOrder = action.payload.targetOrder;
      state.showCreateServiceModal = true;
    },
    setShowCreateQuotationModal: (state, action: PayloadAction<boolean>) => {
      state.showCreateQuotationModal = action.payload;
    },
    setShowGeneratePDFModal: (state, action: PayloadAction<boolean>) => {
      state.showGeneratePDFModal = action.payload;
    },
    openCreateGroupModal: (state, action: PayloadAction<TargetOrder>) => {
      state.newGroupOrder = action.payload.targetOrder;
      state.showCreateGroupModal = true;
    },
    setShowSelectFeatureModal: (state, action: PayloadAction<boolean>) => {
      state.showSelectFeatureModal = action.payload;
    },
    setShowDeleteFeatureModal: (state, action: PayloadAction<boolean>) => {
      state.showDeleteFeatureModal = action.payload;
    },
    setShowCreateGTConfirmationModal: (state, action: PayloadAction<boolean>) => {
      state.showCreateGTConfirmationModal = action.payload;
    },
    setItemIdToCreateGTFrom: (state, action: PayloadAction<string>) => {
      state.itemIdToCreateGTFrom = action.payload;
    },
    setCreateNewItemInside: (state, action: PayloadAction<boolean>) => {
      state.createNewItemInside = action.payload;
    },
    setTreeItems: (state, action: PayloadAction<TreeItem[]>) => {
      state.treeItems = action.payload;
    },
    addOpenedDeatilsItems: (state, action: PayloadAction<ID>) => {
      state.openedDeatilsItems = [...state.openedDeatilsItems, action.payload];
    },
    removeOpenedDeatilsItems: (state, action: PayloadAction<ID>) => {
      const result = state.openedDeatilsItems.filter(item => item !== action.payload);
      state.openedDeatilsItems = [...result];
    },
    setItemToCollapse: (state, action: PayloadAction<ID>) => {
      state.itemToCollapse = action.payload;
    },
    setItemToUncollapse: (state, action: PayloadAction<ID>) => {
      state.itemToUncollapse = action.payload;
    },
    setCollapsedItemsId: (state, action: PayloadAction<UniqueIdentifier[]>) => {
      if (!state.collapsedItemsId.includes(action.payload.toString())) {
        state.collapsedItemsId = [
          ...state.collapsedItemsId,
          ...action.payload,
        ];
      } else {
        const preparedColapsedItemsId = state.collapsedItemsId.filter(item => item !== action.payload.toString());
        state.collapsedItemsId = preparedColapsedItemsId;
      }
    },
    setIsInfoOverlayOpen: (state, action: PayloadAction<boolean>) => {
      state.isInfoOverlayOpen = action.payload;
    },
    setIsProjectLogOverlayOpen: (state, action: PayloadAction<boolean>) => {
      state.isProjectLogOverlayOpen = action.payload;
    },
    setIsCreateItemOverlayOpen: (state, action: PayloadAction<boolean>) => {
      state.isCreateItemOverlayOpen = action.payload;
    },
    setNewProductOrder: (state, action: PayloadAction<number>) => {
      state.newProductOrder = action.payload;
    },
    setIsQuotationReadOnly: (state, action: PayloadAction<boolean>) => {
      state.isQuotationReadOnly = action.payload;
    },
    setProductSuggestionQuantity: (state, action: PayloadAction<number>) => {
      state.productSuggestionQuantity = action.payload;
    },
    setDndGroupId: (state, action: PayloadAction<UniqueIdentifier>) => {
      if (state.dndGroupId !== action.payload) {
        state.dndGroupId = action.payload;
      } else {
        state.dndGroupId = '';
      }
    },
    setIsComposeMode: (state, action: PayloadAction<boolean>) => {
      state.isComposeMode = action.payload;
    },
    setShowFullSummary: (state, action: PayloadAction<boolean>) => {
      state.showFullSummary = action.payload;
    },
    setQuotationPDFName: (state, action: PayloadAction<string>) => {
      state.quotationPDFName = action.payload;
    },
    setIsAnyGroupBeingExported: (state, action: PayloadAction<boolean>) => {
      state.isAnyGroupBeingExported = action.payload;
    },
  },
});

export const { 
  setSelectedGroupFeature, setCopiedItem, setSelectedItem, unsetCopiedItem, unsetSelectedItem,
  setMaximumQuotationUncollapsedDepth, setEstimateMode, setItemToSinglePasteBelow,
  setQuotationItemsTreeLength, unsetItemToSinglePasteBelow, setIsLoadingUpdate,
  setIsGroupSuggestionOpened, setGroupItemIdForSuggestion, setShowDeleteModal,
  setShowCreateGroupModal, openCreateGroupModal, setShowCreateQuotationModal, 
  setShowCreateServiceModal, openCreateServiceModal, setCreateNewItemInside, setTreeItems,
  addOpenedDeatilsItems, removeOpenedDeatilsItems, setItemToCollapse, setItemToUncollapse,
  setIsInfoOverlayOpen, setIsProjectLogOverlayOpen, setIsQuotationReadOnly, setIsCreateItemOverlayOpen, setNewProductOrder,
  setProductSuggestionQuantity, setDndGroupId, setItemToCut, unsetItemToCut, 
  setCollapsedItemsId, setIsComposeMode, setShowFullSummary, setPasteSearchResultsBelowSelectedItem,
  setShowSelectFeatureModal, setFeatureEdit, setActiveQuotationGroupId, setShowDeleteFeatureModal,
  setGroupFeatureIdToEdit, setShowGeneratePDFModal, setQuotationPDFName, setIsAnyGroupBeingExported,
  setShowCreateGTConfirmationModal, setItemIdToCreateGTFrom,
} = quotationSlice.actions;

export default quotationSlice.reducer;